import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, Plus, Trash2, Edit, Cloud, Cpu } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_DEVOPS_SHOWCASE } from '@/lib/adminUtils';

const getDevOpsFromPortfolio = (portfolioData) =>
  portfolioData?.devOpsShowcase?.length
    ? portfolioData.devOpsShowcase
    : DEFAULT_DEVOPS_SHOWCASE;

const AdmDevOpsShowcase = () => {
  const { portfolioData, updateDevOpsShowcase } = usePortfolio();
  const [devOpsItems, setDevOpsItems] = useState(() => getDevOpsFromPortfolio(portfolioData));
  const [isSaving, setIsSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    setDevOpsItems(getDevOpsFromPortfolio(portfolioData));
    setEditingItem(null);
  }, [portfolioData]);

  const initialDevOpsState = {
    title: '',
    description: '',
  };

  const [newDevOps, setNewDevOps] = useState(initialDevOpsState);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updateDevOpsShowcase(devOpsItems);
      if (result?.success) {
        toast.success('DevOps showcase enregistré avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de la sauvegarde du showcase DevOps');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde du showcase DevOps');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setDevOpsItems(getDevOpsFromPortfolio(portfolioData));
    setEditingItem(null);
    setShowAddForm(false);
    setNewDevOps(initialDevOpsState);
    toast.success('Liste réinitialisée');
  };

  const handleDevOpsChange = (index, field, value) => {
    setDevOpsItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleAddDevOps = () => {
    if (!newDevOps.title?.trim() || !newDevOps.description?.trim()) {
      toast.error('Renseignez le titre et la description');
      return;
    }
    setDevOpsItems((prev) => [...prev, { ...newDevOps }]);
    setNewDevOps(initialDevOpsState);
    setShowAddForm(false);
    toast.success('Élément ajouté — pensez à enregistrer');
  };

  const handleDeleteDevOps = (index) => {
    setDevOpsItems((prev) => prev.filter((_, i) => i !== index));
    toast.success('Élément retiré — pensez à enregistrer');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Cpu className="w-6 h-6 mr-2" />
          DevOps Showcase
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-gray-600 dark:text-gray-400">
          {devOpsItems.length} compétence(s) DevOps
        </p>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une compétence
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nouvelle Compétence DevOps</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="new-title" className="flex items-center mb-1">Titre</Label>
              <Input
                id="new-title"
                value={newDevOps.title}
                onChange={(e) => setNewDevOps((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Titre de la compétence"
              />
            </div>
            <div>
              <Label htmlFor="new-description" className="flex items-center mb-1">Description</Label>
              <Textarea
                id="new-description"
                value={newDevOps.description}
                onChange={(e) => setNewDevOps((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Description détaillée"
                rows={3}
              />
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddDevOps}>Ajouter</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {devOpsItems.map((item, index) => (
          <Card key={`${item.title}-${index}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <Cloud className="w-5 h-5 mr-2" />
                  {item.title || 'Sans titre'}
                </CardTitle>
                <div className="flex space-x-2">
                  {editingItem === index ? (
                    <Button variant="outline" size="sm" onClick={() => setEditingItem(null)}>
                      Terminer
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setEditingItem(index)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteDevOps(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor={`title-${index}`} className="flex items-center mb-1">Titre</Label>
                <Input
                  id={`title-${index}`}
                  value={item.title || ''}
                  onChange={(e) => handleDevOpsChange(index, 'title', e.target.value)}
                  disabled={editingItem !== index}
                />
              </div>
              <div>
                <Label htmlFor={`description-${index}`} className="flex items-center mb-1">Description</Label>
                <Textarea
                  id={`description-${index}`}
                  value={item.description || ''}
                  onChange={(e) => handleDevOpsChange(index, 'description', e.target.value)}
                  disabled={editingItem !== index}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {devOpsItems.length === 0 && !showAddForm && (
        <Card>
          <CardContent className="text-center py-8">
            <Cpu className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">Aucun élément DevOps pour le moment</p>
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une compétence
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdmDevOpsShowcase;
